import { observer } from 'mobx-react';
import { ORDER_TYPES } from '@nandosaus/constants';
import { PropTypes } from 'prop-types';
import { restaurantPropTypes } from '@nandosaus/prop-types';
import React from 'react';

import { P } from '../../typography/new';
import { Button } from '../../button/new';
import { deliveryAddressPropTypes } from '../../../prop-types';
import { Flex } from '../../grid';
import { Icon } from '../../icons';
import { OrderSettingsOption } from '../order-settings-option';

const LocationLink = ({
  deliveryAddress,
  deliveryNotes,
  orderType,
  restaurant,
  showDeliveryAddressFinder,
  showRestaurantFinder,
}) => {
  // Both normal delivery and catering delivery show a delivery address instead of a restaurant.
  const isDeliveryLike = orderType === ORDER_TYPES.DELIVERY || orderType === ORDER_TYPES.CATERING_DELIVERY;

  if (isDeliveryLike && deliveryAddress) {
    return (
      <OrderSettingsOption label="Delivery Address" iconName="locationMarker" onClick={showDeliveryAddressFinder}>
        <Flex flex={1} flexWrap="wrap" textAlign="left" flexDirection="column">
          <P variant="standard-semi-bold" mb="4px">
            {deliveryAddress.formattedStreet}
          </P>
          <P variant="extra-small">{deliveryAddress.formattedSuburbStatePostcode}</P>
          {deliveryNotes && (
            <P variant="extra-small" mt="8px" color="grey50">
              {deliveryNotes}
            </P>
          )}
        </Flex>
        {showDeliveryAddressFinder && (
          <Flex justifyContent="flex-end">
            <P variant="extra-small" textDecoration="underline" onClick={showDeliveryAddressFinder}>
              Change
            </P>
          </Flex>
        )}
      </OrderSettingsOption>
    );
  }

  if (isDeliveryLike) {
    return (
      <OrderSettingsOption hasWhiteBg={false} label="Delivery Address">
        <Button variant="tertiary" onClick={showDeliveryAddressFinder} widthType="fullWidth" size="tall" isFullWidth>
          <Icon mr="10px" name="locationMarker" />
          Set delivery address
        </Button>
      </OrderSettingsOption>
    );
  }

  if (restaurant) {
    const formattedStatusMessage = restaurant.getFormattedStatusMessage({ orderType });

    return (
      <OrderSettingsOption label="Restaurant" iconName="barci" onClick={showRestaurantFinder}>
        <Flex flex={1} flexWrap="wrap" textAlign="left" flexDirection="column">
          <P variant="standard-semi-bold" mb="4px">
            {restaurant.name}
          </P>
          {formattedStatusMessage && (
            <P
              variant="extra-small-semi-bold"
              color={restaurant.isOpen && restaurant.orderingEnabled ? 'goGreen' : 'errorPrimary'}
            >
              {formattedStatusMessage.subtitle}
            </P>
          )}
        </Flex>
        {showRestaurantFinder && (
          <Flex justifyContent="flex-end">
            <P variant="extra-small" textDecoration="underline" onClick={showRestaurantFinder}>
              Change
            </P>
          </Flex>
        )}
      </OrderSettingsOption>
    );
  }

  return (
    <OrderSettingsOption hasWhiteBg={false} label="Restaurant">
      <Button size="tall" variant="tertiary" widthType="fullWidth" onClick={showRestaurantFinder} isFullWidth>
        <Icon mr="10px" name="barci" />
        Find a restaurant
      </Button>
    </OrderSettingsOption>
  );
};

LocationLink.propTypes = {
  deliveryAddress: deliveryAddressPropTypes,
  deliveryNotes: PropTypes.string,
  orderType: PropTypes.string.isRequired,
  restaurant: restaurantPropTypes,
  showDeliveryAddressFinder: PropTypes.func.isRequired,
  showRestaurantFinder: PropTypes.func,
};

LocationLink.defaultProps = {
  deliveryAddress: null,
  deliveryNotes: null,
  restaurant: null,
  showRestaurantFinder: undefined,
};

const ComposedLocationLink = observer(LocationLink);
export { ComposedLocationLink as LocationLink };
